import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { useUIStore } from '../store/ui';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { LogOut, Menu, X, Settings, LayoutDashboard, Users, MessageSquare, Book, Calendar, Network, Gift, Building2, SquareUser as UserSquare2, FileText, ListTodo, KeyRound, Mail, MessageCircleQuestion as MessageCircleQuestionIcon, QrCode as QrCodeIcon, ArrowDownSquare, CalendarDays, PersonStanding, ZapOff as ZapOffIcon, ChevronDown } from 'lucide-react';
import ThemeToggle from './ui/ThemeToggle';

const iconMap = {
  LayoutDashboard,
  Users,
  MessageSquare,
  Book,
  Calendar,
  Network,
  Gift,
  Building2,
  UserSquare2,
  FileText,
  ListTodo,
  Mail,
  QrCodeIcon,
  MessageCircleQuestionIcon,
  ArrowDownSquare,
  CalendarDays,
  PersonStanding,
  ZapOffIcon,
};

interface MenuItem {
  id: number;
  profile_id: number;
  group_name: string;
  icon: string;
  title: string;
  url: string;
  created_at: string;
  profile_name: string;
}

function Sidebar() {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const logout = useAuthStore(state => state.logout);
  const user = useAuthStore(state => state.user);
  const { sidebarOpen, setSidebarOpen } = useUIStore();

  const { data: menuData } = useQuery({
    queryKey: ['menus', user?.id, user?.isChildAccount],
    queryFn: async () => {
      if (!user) return { data: [] };
      const endpoint = user.isChildAccount
        ? `/users/menus/restrictions/allowed/${user.id}`
        : '/users/menus';
      const response = await api.get(endpoint);
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 4 * 60 * 1000,
  });

  useEffect(() => {
    if (menuData) {
      // eslint-disable-next-line no-console
      console.log('Menu data loaded:', menuData);
    }
  }, [menuData]);

  const menuItems = Array.isArray(menuData) ? menuData : menuData?.data || [];

  const getIcon = (iconName: string) => {
    return iconMap[iconName as keyof typeof iconMap] || LayoutDashboard;
  };

  const organizeMenuItems = (items: MenuItem[] = []) => {
    const groups: Record<string, MenuItem[]> = {};
    const ungroupedItems: MenuItem[] = [];

    items.forEach(item => {
      if (item.group_name) {
        if (!groups[item.group_name]) {
          groups[item.group_name] = [];
        }
        groups[item.group_name].push(item);
      } else {
        ungroupedItems.push(item);
      }
    });

    return { groups, ungroupedItems };
  };

  const { groups, ungroupedItems } = organizeMenuItems(
    menuItems.filter(item => item.url !== '/settings')
  );

  const toggleGroup = (groupName: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const hasSettingsAccess = menuItems.some(item => item.url === '/settings');
  const showSettingsButton = hasSettingsAccess;

  const handleLogout = () => {
    logout('logout');
    setSidebarOpen(false);
  };

  const renderMenuItem = (item: MenuItem, mobile: boolean = false) => {
    const Icon = getIcon(item.icon);
    const isActive = location.pathname === item.url;

    return (
      <Link
        key={item.id}
        to={item.url}
        className={cn(
          'group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-smooth',
          isActive
            ? 'bg-white/15 text-white shadow-sm backdrop-blur-sm'
            : 'text-white/70 hover:bg-white/10 hover:text-white'
        )}
        onClick={mobile ? () => setSidebarOpen(false) : undefined}
      >
        <Icon
          className={cn(
            'mr-3 h-5 w-5 flex-shrink-0 transition-colors duration-200',
            isActive ? 'text-white' : 'text-white/50 group-hover:text-white/80'
          )}
        />
        {item.title}
      </Link>
    );
  };

  const renderGroup = (groupName: string, items: MenuItem[], mobile: boolean = false) => {
    const isOpen = openGroups[groupName];

    return (
      <div key={groupName}>
        <button
          onClick={() => toggleGroup(groupName)}
          className={cn(
            'group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-smooth',
            'text-white/70 hover:bg-white/10 hover:text-white'
          )}
        >
          <span className="flex items-center">
            <ArrowDownSquare className="mr-3 h-5 w-5 flex-shrink-0 text-white/50 group-hover:text-white/80" />
            {groupName}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-white/40 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </button>
        <div className={cn('ml-3 mt-1 space-y-1 overflow-hidden transition-all duration-300 ease-smooth', isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0')}>
          <div className="border-l border-white/10 pl-3 ml-3 space-y-1">
            {items.map(item => renderMenuItem(item, mobile))}
          </div>
        </div>
      </div>
    );
  };

  const renderNavContent = (mobile: boolean = false) => (
    <>
      {ungroupedItems.map(item => renderMenuItem(item, mobile))}
      {Object.entries(groups).map(([groupName, items]) => renderGroup(groupName, items, mobile))}
    </>
  );

  const sidebarClasses = 'flex min-h-0 flex-1 flex-col bg-gradient-to-b from-primary-700 via-primary-800 to-primary-950 dark:from-primary-800 dark:via-primary-900 dark:to-gray-950';

  const footerButtonClass = (isActive: boolean) =>
    cn(
      'group flex items-center rounded-xl px-2.5 py-2 text-sm font-medium transition-all duration-200 ease-smooth',
      isActive
        ? 'bg-white/15 text-white'
        : 'text-white/70 hover:bg-white/10 hover:text-white'
    );

  const footerIconClass = (isActive: boolean) =>
    cn('h-5 w-5 transition-colors', isActive ? 'text-white' : 'text-white/50 group-hover:text-white/80');

  return (
    <>
      {/* Mobile sidebar */}
      <div
        className={cn('fixed inset-0 z-50 lg:hidden', sidebarOpen ? 'block' : 'hidden')}
        data-mobile-menu
      >
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        <div className="fixed inset-0 z-50 flex animate-fade-in">
          <div className={cn('relative flex w-full max-w-xs flex-1 flex-col', sidebarClasses)}>
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/50 transition-all duration-200"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">Fechar menu</span>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-grow flex-col overflow-y-auto pt-5 pb-4">
              <div className="flex flex-shrink-0 items-center px-4">
                <img className="h-auto w-[54px] max-w-full" src="/logo_branco.png" alt="Logo" />
              </div>
              <nav className="mt-6 flex-1 space-y-1 px-3">{renderNavContent(true)}</nav>
            </div>
            <div className="flex flex-shrink-0 border-t border-white/10 p-4">
              <div className="flex items-center space-x-2">
                <ThemeToggle />
                {showSettingsButton && (
                  <Link
                    to="/settings"
                    className={footerButtonClass(location.pathname === '/settings')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Settings className={footerIconClass(location.pathname === '/settings')} />
                  </Link>
                )}
                <Link
                  to="/change-password"
                  className={footerButtonClass(location.pathname === '/change-password')}
                  onClick={() => setSidebarOpen(false)}
                >
                  <KeyRound className={footerIconClass(location.pathname === '/change-password')} />
                </Link>
                <button
                  onClick={handleLogout}
                  className="group flex items-center rounded-xl px-2.5 py-2 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200 ease-smooth"
                >
                  <LogOut className="h-5 w-5 text-white/50 group-hover:text-white/80" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className={sidebarClasses}>
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <img className="h-auto w-[54px] max-w-full" src="/logo_branco.png" alt="Logo" />
            </div>
            <nav className="mt-6 flex-1 space-y-1 px-3">{renderNavContent()}</nav>
          </div>
          <div className="flex flex-shrink-0 border-t border-white/10 p-4">
            <div className="flex items-center space-x-2">
              <ThemeToggle />
              {showSettingsButton && (
                <Link
                  to="/settings"
                  className={footerButtonClass(location.pathname === '/settings')}
                >
                  <Settings className={footerIconClass(location.pathname === '/settings')} />
                </Link>
              )}
              <Link
                to="/change-password"
                className={footerButtonClass(location.pathname === '/change-password')}
              >
                <KeyRound className={footerIconClass(location.pathname === '/change-password')} />
              </Link>
              <button
                onClick={handleLogout}
                className="group flex items-center rounded-xl px-2.5 py-2 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200 ease-smooth"
              >
                <LogOut className="h-5 w-5 text-white/50 group-hover:text-white/80" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
